<template>
  <div class="verification-required-sheet">
      <div class="verification-required-sheet__layout">
        <div class="verification-required-sheet__panel">
          <div
              aria-hidden="true"
              class="verification-required-sheet__icon">
            <svg
                class="h-5 w-5 fill-current text-brand-black"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512">
              <path
                  d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
            </svg>
          </div>

          <div class="verification-required-sheet__copy">
            <h2 class="verification-required-sheet__title">Verification Required</h2>
            <p class="verification-required-sheet__message">{{ text }}</p>
          </div>

          <div class="verification-required-sheet__actions">
            <button
                type="button"
                class="verification-required-sheet__cta"
                @click="startVerification">
              Verify now
            </button>
          </div>
        </div>
      </div>
  </div>
</template>

<script setup lang="ts">
import {modalController} from '@ionic/vue';
import VerificationSheet from '@/views/common/VerificationSheet.vue';

defineProps({
  text: {
    type: String,
    default: 'You must be verified to perform this action.',
  },
});

const startVerification = async () => {
  await modalController.dismiss();

  const sheet = await modalController.create({
    component: VerificationSheet,
    backdropDismiss: false,
    canDismiss: true,
    cssClass: 'verification-fullscreen-modal',
    mode: 'ios',
    id: 'verificationSheet',
  });

  await sheet.present();
};
</script>

<style scoped>
.verification-required-sheet {
  background: #1A1B22;
  width: 100%;
}

.verification-required-sheet__layout {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding:
      max(1.5rem, env(safe-area-inset-top, 0px))
      max(1rem, env(safe-area-inset-right, 0px))
      max(1.5rem, env(safe-area-inset-bottom, 0px))
      max(1rem, env(safe-area-inset-left, 0px));
}

.verification-required-sheet__panel {
  box-sizing: border-box;
  display: flex;
  width: 100%;
  max-width: 28rem;
  flex-direction: column;
  align-items: center;
  gap: clamp(1.25rem, 4vw, 1.75rem);
  margin: 0 auto;
}

.verification-required-sheet__icon {
  display: flex;
  height: clamp(2.5rem, 8vw, 3rem);
  width: clamp(2.5rem, 8vw, 3rem);
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #A5A7AF;
  color: #1A1B22;
}

.verification-required-sheet__copy {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
}

.verification-required-sheet__title {
  margin: 0;
  color: #fff;
  font-size: clamp(1.25rem, 4.5vw, 1.75rem);
  font-weight: 500;
  line-height: 1.2;
}

.verification-required-sheet__message {
  margin: 0;
  max-width: 24rem;
  overflow-wrap: anywhere;
  color: #A5A7AF;
  font-size: clamp(0.95rem, 3.6vw, 1.125rem);
  line-height: 1.45;
}

.verification-required-sheet__cta {
  box-sizing: border-box;
  display: inline-flex;
  width: min(100%, 16rem);
  min-height: 3rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: #EBFF00;
  padding: 0.75rem 2rem;
  color: #1A1B22;
  font-size: clamp(1rem, 3.8vw, 1.25rem);
  font-weight: 500;
  line-height: 1.2;
}

.verification-required-sheet__actions {
  display: flex;
  width: 100%;
  justify-content: center;
}

@media (min-width: 768px) {
  .verification-required-sheet__layout {
    padding: 1.5rem;
  }

  .verification-required-sheet__panel {
    gap: 1.75rem;
  }

  .verification-required-sheet__cta {
    width: auto;
    min-width: 14rem;
  }
}

@media (max-height: 520px) and (orientation: landscape) {
  .verification-required-sheet__layout {
    align-items: flex-start;
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
  }

  .verification-required-sheet__panel {
    gap: 1rem;
  }
}
</style>
